import { useState } from 'react'
import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { MetricDeltas } from '@/components/sales-report/components/MetricDeltas'
import type { Invoicing } from '../types'
import { MONO, fmt, monthLabel } from './format'
import { MonthBars } from './MonthBars'

type Comparison = { current: number; prevMonth: number | null; prevYear: number | null; prevYearLabel: string; prevLabel: string }

const CARD = 'min-w-0 rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900'
const SLATE = '#5E6E8C'
const CHAMP = '#AE9056'

/** Planning Centre invoicing — invoiced (recognised) vs pipeline. Hover a month
 *  for its totals; a year band marks the period. */
export function InvoicingCard({
  invoicing,
  currency,
  months,
  comparison,
}: {
  invoicing: Invoicing
  currency: string
  months: string[]
  comparison: Comparison
}) {
  const [hov, setHov] = useState<number | null>(null)
  const max = Math.max(1, ...invoicing.invoiced, ...invoicing.pipeline)
  const invoicedTotal = invoicing.invoiced.reduce((a, b) => a + b, 0)
  const pipelineTotal = invoicing.pipeline.reduce((a, b) => a + b, 0)
  // Of the invoiced total, how much came from PC cases placed before the window (Query N4).
  const priorYear = invoicing.priorYearDelivered ?? 0
  // Headline reads the hovered month, else the whole-range total at rest — the
  // same hover behaviour as the Credit utilisation card.
  const invoicedShown = hov != null ? invoicing.invoiced[hov] : invoicedTotal
  const pipelineShown = hov != null ? invoicing.pipeline[hov] : pipelineTotal
  const span = months.length > 1 ? `${monthLabel(months[0])} – ${monthLabel(months[months.length - 1])}` : monthLabel(months[0])
  const caption = hov != null ? monthLabel(months[hov]) : span

  return (
    <div className={CARD}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 dark:text-stone-100">
          Planning Centre invoicing
          <InfoPopover term="inv" />
        </h3>
        <span className="text-[11px] text-stone-400 dark:text-stone-500">invoiced vs pipeline · {caption}</span>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-x-8 gap-y-4">
        <div>
          <p className={cn('text-4xl font-semibold tracking-tight', MONO)}>{fmt(invoicedShown, currency)}</p>
          <p className="inline-flex items-center gap-1.5 text-[11px] text-stone-400 dark:text-stone-500">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: SLATE }} />
            invoiced (recognised)
          </p>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          <div>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] text-stone-400 dark:text-stone-500">
              <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: CHAMP }} />
              Pipeline
              <InfoPopover term="pipe" />
            </span>
            <p className={cn('mt-1 text-sm font-medium', MONO)}>{fmt(pipelineShown, currency)}</p>
          </div>
        </div>
      </div>
      <MetricDeltas current={invoicedTotal} prevMonth={comparison.prevMonth} prevYear={comparison.prevYear} prevYearLabel={comparison.prevYearLabel} prevLabel={comparison.prevLabel} currency={currency} />

      <div className="mt-5">
        <MonthBars months={months} onHover={setHov} isActive={(i) => hov === i}>
          {(i) => {
            const on = hov === i
            return (
              <div className="flex w-full items-end justify-center gap-0.5" style={{ height: 76 }}>
                <div className="min-w-0 flex-1 rounded-t" style={{ height: Math.round((invoicing.invoiced[i] / max) * 76), backgroundColor: SLATE, opacity: on ? 1 : 0.7 }} />
                <div className="min-w-0 flex-1 rounded-t" style={{ height: Math.round((invoicing.pipeline[i] / max) * 76), backgroundColor: CHAMP, opacity: on ? 1 : 0.65 }} />
              </div>
            )
          }}
        </MonthBars>
      </div>

      <p className="mt-3 text-[11px] text-stone-400 dark:text-stone-500">
        Net of PC credit.{' '}
        {priorYear > 0.5 ? (
          <>
            Invoiced includes <span className={MONO}>{fmt(priorYear, currency)}</span> from PC cases placed before the window (Jan-25–May-26) and completed within it; the window cohort (invoiced − that) + pipeline tie to True Sale.{' '}
          </>
        ) : (
          <>Invoiced + pipeline tie to True Sale.{' '}</>
        )}
        Hover a bar for that month&rsquo;s invoiced and pipeline.
      </p>
    </div>
  )
}
